import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import logger from '../utils/logger';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${BACKEND_URL}/api/auth/me`, {
        withCredentials: true
      });
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password, rememberMe = false) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/login`, 
      { email, password, remember_me: rememberMe },
      { withCredentials: true }
    );
    // 2FA step-up: don't set user yet — caller handles the second factor flow
    if (response.data?.requires_2fa) {
      return response.data;
    }
    setUser(response.data);
    return response.data;
  };

  const verify2FA = async (pendingToken, code, rememberMe = false) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/verify`,
      { pending_token: pendingToken, code, remember_me: rememberMe },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const request2FAEmailCode = async (pendingToken) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/2fa/email-code`,
      { pending_token: pendingToken },
      { withCredentials: true }
    );
    return response.data;
  };

  const register = async (name, email, password) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/register`, 
      { name, email, password },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const googleLogin = async (sessionId) => {
    const response = await axios.post(`${BACKEND_URL}/api/auth/google/session`, 
      { session_id: sessionId },
      { withCredentials: true }
    );
    setUser(response.data);
    return response.data;
  };

  const logout = async () => {
    try {
      await axios.post(`${BACKEND_URL}/api/auth/logout`, {}, { withCredentials: true });
    } catch (error) {
      logger.error('Logout error:', error);
    }
    setUser(null);
  };

  const value = {
    user,
    loading,
    login,
    register,
    googleLogin,
    logout,
    checkAuth,
    verify2FA,
    request2FAEmailCode,
    // Accept all admin roles from RBAC system
    isAdmin: ['admin', 'super_admin', 'content_admin', 'finance_admin', 'events_admin', 'viewer'].includes(user?.role)
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
